<script setup>
import Loading from '../../Loading.vue';
import { computed, onMounted, onUnmounted, reactive, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import SafeRichText from '../../communications/SafeRichText.vue';
import { shortDate, timeOfDay, toLocalDate } from '../../../helpers/usDate';
import { usesMergeFields, withSamples } from '../../communications/mergeFieldSamples';
import { getHistoryEntry, listHistory } from '../../../api/history';
import { channelChips } from '../../communications/channelHelpers';

import XlButton from '../../xl/XlButton.vue';
import CommsInfoBanner from '../../communications/CommsInfoBanner.vue';
import { COMMS_HINTS } from './hints';
import XlCard from '../../xl/XlCard.vue';
import XlDropdown from '../../xl/XlDropdown.vue';
import XlPagination from '../../xl/XlPagination.vue';
import XlTable from '../../xl/XlTable.vue';
import XlTag from '../../xl/XlTag.vue';
import XlTooltip from '../../xl/XlTooltip.vue';
import { TERM_TIPS } from '../../../helpers/termTips';

const PER_PAGE = 10;
const POLL_MS = 20000;
const SOURCE_OPTIONS = [
  { value: 'all', label: 'All sources' },
  { value: 'sequence', label: 'Sequence steps', tooltip: TERM_TIPS['Sequence steps'] },
  { value: 'adhoc', label: 'Ad hoc', tooltip: TERM_TIPS['Ad hoc'] },
];

import { useAuthStore } from '../../../stores/auth';
import { useCommunicationsStore } from '../../../stores/communications';
import { canAssignBrokers } from '../../../helpers/roles';

const auth = useAuthStore();
const store = useCommunicationsStore();
const route = useRoute();
const router = useRouter();
// Owner and hub staff look at the whole agency; a broker only ever sees their
// own book, and the page should say which of the two it is showing.
const seesWholeAgency = computed(() => canAssignBrokers(auth.userData));

// The send that just brought the reader here from Send a message.
const justSent = computed(() => Number(route?.query?.sent) || null);

const entries = ref([]);
const meta = reactive({ total: 0, currentPage: 1, lastPage: 1 });
const sourceFilter = ref('all');
const sentByFilter = ref('');
const isFiltered = computed(() => sourceFilter.value !== 'all' || sentByFilter.value !== '');
const senders = ref([]);
const loadingList = ref(true);
const listError = ref('');

const selectedEntry = ref(null);
const detailLoading = ref(false);
const detailError = ref('');
const isDetailOpen = ref(false);

let latestRequest = 0;

// A quiet fetch updates the rows in place: no loading line, and a failed one
// keeps the rows it has rather than swapping the table for an error.
const fetchPage = async (page = 1, { quiet = false } = {}) => {
  const request = ++latestRequest;
  if (!quiet) {
    loadingList.value = true;
    listError.value = '';
  }
  try {
    const { data, meta: pageMeta } = await listHistory({
      source: sourceFilter.value,
      sentBy: sentByFilter.value,
      page,
      perPage: PER_PAGE,
    });
    if (request !== latestRequest) return;
    listError.value = '';
    entries.value = data;
    senders.value = pageMeta.senders ?? senders.value;
    meta.total = pageMeta.total;
    meta.currentPage = pageMeta.current_page;
    meta.lastPage = pageMeta.last_page;
  } catch (e) {
    if (!quiet) listError.value = 'Could not load history.';
  } finally {
    if (request === latestRequest) loadingList.value = false;
  }
};

// Sends, failures and opens land from queued jobs after a row first shows.
const refresh = () => {
  if (document.hidden || isDetailOpen.value || loadingList.value) return;
  fetchPage(meta.currentPage, { quiet: true });
};

const changeSource = (value) => {
  sourceFilter.value = value;
  fetchPage(1);
};

const changeSentBy = (value) => {
  sentByFilter.value = value ?? '';
  fetchPage(1);
};

const senderOptions = computed(() => [
  { value: '', label: 'Sent by anyone' },
  ...senders.value.map((sender) => ({ value: sender.id, label: senderLabel(sender.name, sender.role) })),
]);

const goToPage = (page) => {
  if (page < 1 || page > meta.lastPage) return;
  fetchPage(page);
};

const pageFrom = computed(() => (meta.currentPage - 1) * PER_PAGE + 1);

// A broker sends as themselves; the owner and hub staff send for the agency, so
// the record says in which capacity.
const ROLE_PREFIX = { owner: 'Owner', 'event-planner': 'Hub staff' };

const senderLabel = (name, role) => [ROLE_PREFIX[role], name].filter(Boolean).join(' · ');

const attributionLabel = (entry) => (entry.sent_by_name
  ? senderLabel(entry.sent_by_name, entry.sent_by_role)
  : ['Automatic', entry.sequence_name].filter(Boolean).join(' · '));

const FAILURE_REASONS = {
  no_email: 'no email address on file',
  refused: 'refused by the mail server',
  error: 'sending error',
};

const failedText = (entry) => {
  const reasons = entry.failure_reasons ?? [];
  const total = countOf(entry.failed_count ?? 0);
  if (!entry.failed_count || !reasons.length) return total;

  const parts = reasons.map(({ reason, count }) => {
    const label = FAILURE_REASONS[reason] ?? reason;
    return reasons.length === 1 ? label : `${count.toLocaleString('en-US')} ${label}`;
  });
  return `${total} — ${parts.join(' · ')}`;
};

const sourceLabel = (entry) => ({ adhoc: 'Ad hoc message', session: 'Session update' }[entry.source] ?? 'Sequence message');

/** "Email + message": one phrase, so only its first word is capitalised. */
const channelLine = (entry) => channelChips(entry.channels)
  .map((label, index) => (index ? label.toLowerCase() : label))
  .join(' + ');

const sourceText = (entry) => ({ adhoc: 'Ad hoc', session: 'Session cancelled' }[entry.source] ?? 'Sequence step');

/** "Ad hoc · Email", or the sequence step that produced it. */
const originLine = (entry) => [sourceText(entry), channelLine(entry)].filter(Boolean).join(' · ');

const audienceLine = (entry) => {
  if (entry.source === 'session') return 'People with a seat';

  return entry.segment_names?.length ? entry.segment_names.join(', ') : 'Everyone';
};

const countOf = (value) => (Number.isFinite(value) ? value.toLocaleString('en-US') : '—');

/**
 * Only for a send that actually reached somebody by email: only email carries
 * a pixel, so a chat-only message has no rate, and a rate over nothing is not
 * 0% — it is unknown. An email + chat send emails every recipient as well, and
 * a failed email comes off sent_count, so the rate holds there too.
 */
const openRate = (entry) => {
  if (!Number.isFinite(entry.opened_count) || !entry.sent_count) return '';
  if (!(entry.channels ?? []).includes('email')) return '';

  return `${Math.min(100, Math.round((entry.opened_count / entry.sent_count) * 100))}%`;
};

/**
 * How many it went to, and how many it was aimed at when those differ —
 * suppressions and failures make them diverge, and only showing the first
 * hides that anyone was missed.
 */
const aimedAtMore = (entry) =>
  Number.isFinite(entry.recipient_count)
  && Number.isFinite(entry.sent_count)
  && entry.recipient_count !== entry.sent_count;

// On the viewer's own clock, the way they remember sending it; "Today" is
// their today. "Aug 10, 2026, 9:00 AM".
const formatDate = (value) => {
  const date = toLocalDate(value);
  if (!date) return '';
  if (date.toDateString() === new Date().toDateString()) return `Today, ${timeOfDay(date)}`;
  return shortDate(date, { time: true });
};


// One row can cover recipients whose merge values all differ, so a real or
// sample substitution here would be a false claim for everyone but one
// recipient. Placeholder text is the only rendering that stays true for all.
const unresolveMergeFields = (value) =>
  (value ?? '').replace(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g, (match, field) => {
    const words = field.split('_').filter(Boolean);
    if (!words.length) return match;
    const [firstWord, ...rest] = words;
    const label = [
      firstWord.charAt(0).toUpperCase() + firstWord.slice(1).toLowerCase(),
      ...rest.map((word) => word.toLowerCase()),
    ].join(' ');
    return `[${label}]`;
  });

const rowSubject = (entry) => unresolveMergeFields(entry.subject) || sourceLabel(entry);

const detailSubject = computed(() => (selectedEntry.value ? rowSubject(selectedEntry.value) : ''));

const detailMeta = computed(() => {
  const entry = selectedEntry.value;

  return `${sourceText(entry)} · sent ${formatDate(entry.created_at)} by ${attributionLabel(entry)}`;
});

const fromLine = computed(() => {
  const from = selectedEntry.value?.from;
  return from?.address ? `${from.name} <${from.address}>` : '';
});

// One row covers everybody it went to, each with their own details, so the
// letter is shown filled with samples and says so — the same as the composer.
const hasFields = computed(() => usesMergeFields(`${selectedEntry.value?.subject} ${selectedEntry.value?.body_html}`));

const segmentLabel = computed(() => (selectedEntry.value?.segment_names?.length > 1 ? 'Segments' : 'Segment'));

const sendSimilar = () => {
  router.replace({ query: { tab: 'send', similar: selectedEntry.value.id } });
};

const viewEntry = async (row) => {
  isDetailOpen.value = true;
  detailLoading.value = true;
  detailError.value = '';
  selectedEntry.value = null;
  try {
    selectedEntry.value = await getHistoryEntry(row.id);
  } catch (e) {
    detailError.value = 'Could not load this message.';
  } finally {
    detailLoading.value = false;
  }
};

const closeDetail = () => {
  isDetailOpen.value = false;
  selectedEntry.value = null;
  refresh();
};

// A message's record replaces the tab strip, as an editor does.
watch(isDetailOpen, (open) => store.setEditing(open));

let poll = null;

onMounted(() => {
  fetchPage(1);
  poll = setInterval(refresh, POLL_MS);
  document.addEventListener('visibilitychange', refresh);
});
onUnmounted(() => {
  clearInterval(poll);
  document.removeEventListener('visibilitychange', refresh);
  store.setEditing(false);
});
</script>

<template>
  <div class="history-tab">
    <div v-if="!isDetailOpen">
      <CommsInfoBanner :text="COMMS_HINTS.history" />

      <div class="history-tab__filters">
        <div class="history-tab__sources" role="group" aria-label="Filter by source">
          <XlButton
            v-for="option in SOURCE_OPTIONS"
            :key="option.value"
            variant="ghost"
            size="sm"
            class="history-tab__pill"
            :class="{ 'is-on': sourceFilter === option.value }"
            :aria-pressed="sourceFilter === option.value"
            :data-testid="`history-source-${option.value}`"
            @click="changeSource(option.value)"
          >
            <XlTooltip v-if="option.tooltip" :text="option.tooltip" placement="bottom" control underline>{{ option.label }}</XlTooltip>
            <template v-else>{{ option.label }}</template>
          </XlButton>
        </div>
        <XlDropdown
          v-if="seesWholeAgency"
          class="history-tab__sender"
          data-testid="history-sender-filter"
          aria-label="Filter by who sent it"
          :options="senderOptions"
          :model-value="sentByFilter"
          @update:model-value="changeSentBy"
        />

        <p class="history-tab__retention">
          {{ seesWholeAgency
            ? 'Showing the last 90 days, whole agency'
            : 'Showing the last 90 days, your policyholders only' }}
        </p>
      </div>

      <p v-if="listError" role="alert">{{ listError }}</p>
      <Loading v-else-if="loadingList" variant="table" :rows="6" data-testid="history-loading" />
      <p v-else-if="!entries.length && !isFiltered" class="history-tab__empty">Nothing has gone out yet.</p>

      <XlCard v-else class="history-tab__table">
        <XlTable>
        <template #head>
          <th>Subject</th>
          <th>Sent to</th>
          <th>Sent</th>
          <th><XlTooltip :text="TERM_TIPS.Opened" placement="bottom" underline>Opened</XlTooltip></th>
          <th>When</th>
          <th>By</th>
          <th></th>
        </template>

        <tr
          v-for="row in entries"
          :key="row.id"
          class="is-clickable"
          data-testid="history-row"
          tabindex="0"
          role="button"
          :aria-label="`Open ${rowSubject(row)}`"
          @click="viewEntry(row)"
          @keydown.enter.prevent="viewEntry(row)"
          @keydown.space.prevent="viewEntry(row)"
        >
          <td>
            <b>{{ rowSubject(row) }}</b>
            <span class="history-tab__origin">
              {{ originLine(row) }}<XlTag v-if="row.id === justSent" tone="success" class="history-tab__new" data-testid="history-row-new">Just sent</XlTag>
            </span>
          </td>

          <td class="history-tab__muted">{{ audienceLine(row) }}</td>

          <td>
            <span data-testid="history-recipient-count">
              {{ countOf(row.sent_count) }}<span v-if="aimedAtMore(row)" class="history-tab__rate"> of {{ countOf(row.recipient_count) }}</span>
            </span>
            <span v-if="row.failed_count || row.suppressed_count" class="history-tab__delivery">
              <span v-if="row.failed_count" class="history-tab__failed" data-testid="history-failed-count">
                {{ row.failed_count }} failed
              </span>
              <span v-if="row.suppressed_count" data-testid="history-suppressed-count">
                {{ row.suppressed_count }} suppressed
              </span>
            </span>
          </td>

          <td class="history-tab__opened" data-testid="history-opened">
            <template v-if="Number.isFinite(row.opened_count)">
              {{ countOf(row.opened_count) }}
              <span v-if="openRate(row)" class="history-tab__rate">{{ openRate(row) }}</span>
            </template>
            <span v-else class="history-tab__muted">—</span>
          </td>

          <td class="history-tab__muted">{{ formatDate(row.created_at) }}</td>
          <td class="history-tab__muted">{{ attributionLabel(row) }}</td>
          <td><svg class="history-tab__chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m9.8 18 6-6-6-6" /></svg></td>
        </tr>
        <tr v-if="!entries.length" data-testid="history-no-match">
          <td colspan="7" class="history-tab__muted">Nothing matches these filters.</td>
        </tr>
        </XlTable>
      </XlCard>

      <div v-if="entries.length && meta.lastPage > 1" class="history-tab__pager">
        <XlPagination
          :from="pageFrom"
          :to="pageFrom + entries.length - 1"
          :total="meta.total"
          @previous="goToPage(meta.currentPage - 1)"
          @next="goToPage(meta.currentPage + 1)"
        />
      </div>
    </div>

    <div v-else class="history-detail" data-testid="history-detail">
      <XlButton
        variant="ghost"
        size="sm"
        class="history-detail__back"
        data-testid="history-detail-back"
        @click="closeDetail"
      >
        <template #icon>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="m14.2 18-6-6 6-6" />
          </svg>
        </template>
        Back to history
      </XlButton>

      <Loading v-if="detailLoading" variant="detail" data-testid="history-detail-loading" />
      <p v-else-if="detailError" role="alert">{{ detailError }}</p>
      <template v-else-if="selectedEntry">
        <h2 class="history-detail__title">{{ detailSubject }}</h2>
        <p class="history-detail__meta" data-testid="history-detail-meta">{{ detailMeta }}</p>

        <div class="history-detail__grid">
          <XlCard padded class="history-detail__card">
            <h3 class="history-detail__card-title">What was sent</h3>
            <div class="history-detail__paper">
              <div class="history-detail__envelope" data-testid="history-detail-envelope">
                <div v-if="fromLine"><b>From:</b> {{ fromLine }}</div>
                <div><b>Channel:</b> {{ channelLine(selectedEntry) }}</div>
              </div>
              <div class="history-detail__letter">
                <b class="history-detail__subject">{{ withSamples(selectedEntry.subject) || sourceLabel(selectedEntry) }}</b>
                <SafeRichText
                  class="history-detail__body"
                  data-testid="history-detail-body"
                  :html="withSamples(selectedEntry.body_html)"
                />
              </div>
            </div>
            <p class="history-detail__note" data-testid="history-detail-note">
              <template v-if="hasFields">This is a permanent record — the message as it went out. Personal details are shown with sample values; each person received their own.</template>
              <template v-else>This is a permanent record — the message as each person received it.</template>
            </p>
          </XlCard>

          <XlCard padded class="history-detail__card">
            <h3 class="history-detail__card-title">How it went</h3>
            <div class="history-detail__stats">
              <div>
                <span class="history-detail__stat-label">Sent to</span>
                <b class="history-detail__stat" data-testid="history-detail-sent">
                  {{ countOf(selectedEntry.sent_count) }}<small v-if="aimedAtMore(selectedEntry)"> of {{ countOf(selectedEntry.recipient_count) }}</small>
                </b>
              </div>
              <div>
                <span class="history-detail__stat-label">Opened</span>
                <b
                  class="history-detail__stat"
                  :class="{ 'history-detail__stat--good': openRate(selectedEntry) }"
                  data-testid="history-detail-opened"
                >{{ openRate(selectedEntry) || '—' }}</b>
              </div>
            </div>
            <div v-if="openRate(selectedEntry)" class="history-detail__bar">
              <i :style="{ width: openRate(selectedEntry) }" />
            </div>

            <dl class="history-detail__facts" :class="{ 'history-detail__facts--spaced': !openRate(selectedEntry) }">
              <div>
                <dt>{{ segmentLabel }}</dt>
                <dd>{{ audienceLine(selectedEntry) }}</dd>
              </div>
              <div>
                <dt>Source</dt>
                <dd>{{ sourceText(selectedEntry) }}</dd>
              </div>
              <div>
                <dt>Failed</dt>
                <dd :class="{ 'history-tab__failed': selectedEntry.failed_count }" data-testid="history-detail-failed">
                  {{ failedText(selectedEntry) }}
                </dd>
              </div>
              <div v-if="selectedEntry.suppressed_count">
                <dt>Suppressed</dt>
                <dd>{{ countOf(selectedEntry.suppressed_count) }}</dd>
              </div>
            </dl>

            <XlButton
              variant="ghost"
              class="history-detail__similar"
              data-testid="history-detail-similar"
              @click="sendSimilar"
            >Send something similar</XlButton>
          </XlCard>
        </div>
      </template>
    </div>
  </div>
</template>

<style scoped lang="scss">
.history-tab {
  display: flex;
  flex-direction: column;
  gap: 12px;
  color: var(--xl-color-primary-text);

  &__filters {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
    margin-bottom: 16px;
  }

  &__retention {
    margin: 0 0 0 auto;
    font-size: 13px;
    color: var(--xl-color-outline);
  }

  &__sources {
    display: flex;
    gap: 7px;
    flex-wrap: wrap;
  }

  &__sources &__pill {
    height: 36px;
    border: 1px solid var(--xl-color-border);
    background: #fff;
    box-shadow: none;
    color: var(--xl-color-on-surface-variant);

    &:hover:not(:disabled) {
      transform: none;
      border-color: var(--xl-color-primary-fixed-dim);
      background: var(--xl-color-primary-container);
      box-shadow: none;
      color: var(--xl-color-primary);
    }

    &.is-on,
    &.is-on:hover:not(:disabled) {
      border-color: var(--xl-color-primary);
      background: var(--xl-color-primary);
      color: #fff;
    }
  }

  // The design's filter row is 38px tall; its 42px control hangs 4px below
  // the row instead of pushing the table down.
  &__sender { width: auto; margin-bottom: -4px; }

  &__origin {
    display: block;
    font-size: var(--xl-type-body-small-size);
    color: var(--xl-color-outline);
  }

  &__table td.history-tab__muted { color: var(--xl-color-outline); }

  &__rate {
    font-size: var(--xl-type-body-small-size);
    color: var(--xl-color-outline);
  }

  // The count and its rate are one reading, so they never break across lines.
  &__opened { white-space: nowrap; }

  &__delivery {
    display: block;
    font-size: var(--xl-type-body-small-size);
    color: var(--xl-color-outline);

    // One per line: side by side they broke mid-phrase in a column this narrow.
    > * { display: block; white-space: nowrap; }
  }

  &__failed { color: var(--xl-color-error); }

  &__chevron { display: block; }

  &__empty,
  &__loading {
    margin: 0;
    color: var(--xl-color-outline);
    font-size: 14px;
  }

  &__pager {
    display: flex;
    justify-content: center;
    margin-top: 16px;
  }

  &__new { margin-left: 8px; vertical-align: middle; }
}

// The prototype's histOne(): the letter on the left, what became of it on the right.
.history-detail {
  color: var(--xl-color-primary-text);

  &__back { margin-bottom: 18px; }

  &__title {
    margin: 0 0 6px;
    font-size: 24px;
    font-weight: 600;
    letter-spacing: -0.03em;
    color: var(--xl-color-primary-text);
  }

  &__meta {
    margin: 0 0 20px;
    font-size: 14px;
    color: var(--xl-color-outline);
  }

  &__grid {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    gap: 12px;
    align-items: start;
  }

  @media (max-width: 900px) {
    &__grid { grid-template-columns: minmax(0, 1fr); }
  }

  &__card-title {
    margin: 0;
    font-size: 15px;
    font-weight: 700;
    line-height: 22px;
    color: var(--xl-color-primary-text);
  }

  &__paper {
    margin-top: 14px;
    overflow: hidden;
    border: 1px solid var(--xl-color-border);
    border-radius: var(--xl-radius-lg);
    background: var(--xl-color-background);
    box-shadow: var(--xl-shadow-1);
  }

  &__envelope {
    padding: 14px 16px;
    border-bottom: 1px solid var(--xl-color-border);
    font-size: 12.5px;
    color: var(--xl-color-outline);
    overflow-wrap: anywhere;

    b { color: var(--xl-color-primary-text); }
    div + div { margin-top: 3px; }
  }

  &__letter { padding: 16px; }

  &__subject {
    display: block;
    margin-bottom: 10px;
    font-size: 15px;
    color: var(--xl-color-primary-text);
  }

  &__body {
    font-size: 14px;
    line-height: 1.6;
    color: var(--xl-color-secondary-text);
    overflow-wrap: anywhere;

    :deep(p) { margin: 0 0 10px; }
    :deep(p:last-child) { margin-bottom: 0; }
    :deep(ul), :deep(ol) { margin: 0 0 10px; padding-left: 20px; }
  }

  &__note {
    margin: 12px 0 0;
    font-size: 12.5px;
    color: var(--xl-color-outline);
  }

  &__stats {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
    margin-top: 14px;
  }

  &__stat-label {
    display: block;
    font-size: 12.5px;
    color: var(--xl-color-outline);
  }

  &__stat {
    display: block;
    font-size: 26px;
    font-weight: 600;
    letter-spacing: -0.03em;
    color: var(--xl-color-primary-text);

    small { font-size: 13px; font-weight: 400; letter-spacing: 0; color: var(--xl-color-outline); }

    &--good { color: var(--xl-color-success); }
  }

  &__bar {
    height: 6px;
    margin-top: 14px;
    overflow: hidden;
    border-radius: 99px;
    background: var(--xl-color-border-subtle);

    i {
      display: block;
      height: 100%;
      border-radius: 99px;
      background: var(--xl-color-success);
    }
  }

  &__facts {
    margin: 0;

    // Where no bar sits between them, the figures still need the bar's room.
    &--spaced { margin-top: 20px; }

    > div {
      display: flex;
      align-items: center;
      gap: 14px;
      padding: 8px 0;
      border-bottom: 1px solid var(--xl-color-border-subtle);
    }

    dt { flex: 0 0 150px; font-size: 13.5px; color: var(--xl-color-outline); }

    dd {
      flex: 1;
      min-width: 0;
      margin: 0;
      font-weight: 500;
      color: var(--xl-color-primary-text);
    }
  }

  &__similar { width: 100%; margin-top: 16px; }
}
</style>
